import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {View, Text, TouchableOpacity, StyleSheet} from 'react-native';
import {connect} from 'react-redux';
import Iframe from 'react-iframe';

import getRemoteConfigValue from 'utils/firebase/remoteConfig';
import {M3D_SCENES_BASE_URL} from 'utils/World/config';

class PlatformWorld extends Component {
  static instance = undefined;

  constructor(props) {
    super(props);
    this.state = {iframeFailed: false, iframeFailReason: null};
    this.watchdogTimer = null;
  }

  componentDidMount() {
    console.log('[WEBGL_DBG] PlatformWorld.componentDidMount');
    PlatformWorld.instance = this;
    this.iframe = document.getElementById('world-iframe');
    if (this.iframe) {
      this.iframe.style.border = 'none';
    }

    this.handleMessage = (event) => {
      if (this.iframe && event.source !== this.iframe.contentWindow) {
        return; // ignore messages from other iframes/windows
      }
      console.log(
        '[WEBGL_DBG] msg-recv route=' +
          (event.data && event.data.route) +
          ' command=' +
          (event.data && event.data.command) +
          ' action=' +
          (event.data && event.data.action),
      );
      // Any postMessage from our iframe means Unity is alive — clear watchdog.
      this.cancelWatchdog();
      PlatformWorld.onWindowMessage(event, this);
    };
    window.addEventListener('message', this.handleMessage);

    // Watchdog: if no Unity ready signal arrives within 30s, surface the
    // failure UI instead of leaving the user stuck on a permanent loader.
    this.watchdogTimer = setTimeout(() => {
      console.warn(
        '[WEBGL_DBG] iframe watchdog 30s elapsed without Unity ready signal',
      );
      this.setState({iframeFailed: true, iframeFailReason: 'timeout'});
    }, 30000);

    if (this.iframe) {
      this.iframe.addEventListener('load', this.handleIframeLoad);
      this.iframe.addEventListener('error', (e) => {
        console.error('[WEBGL_DBG] iframe error event', e);
        this.setState({iframeFailed: true, iframeFailReason: 'load_error'});
      });
    }
  }

  componentWillUnmount() {
    console.log('[WEBGL_DBG] PlatformWorld.componentWillUnmount');
    PlatformWorld.instance = undefined;
    if (this.handleMessage) {
      window.removeEventListener('message', this.handleMessage);
    }
    this.cancelWatchdog();
  }

  cancelWatchdog = () => {
    if (this.watchdogTimer) {
      clearTimeout(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  };

  handleIframeLoad = () => {
    this.cancelWatchdog();
    console.log('[WEBGL_DBG] iframe load fired (network ok, html parsed)');
  };

  handleRetry = () => {
    this.setState({iframeFailed: false, iframeFailReason: null});
    window.location.reload();
  };

  static onWindowMessage(event) {
    const {instance} = PlatformWorld;
    if (!instance) {
      return;
    }

    try {
      // TODO: Check for source, if it is our local 3d world, relay
      // that message out to other users
      // HACK:
      if (event.data) {
        const hasAction = Object.prototype.hasOwnProperty.call(
          event.data,
          'action',
        );
        const hasRoute = Object.prototype.hasOwnProperty.call(
          event.data,
          'route',
        );

        if (hasAction || hasRoute) {
          instance.onMessageFromWorld(event.data);
        }
      }
    } catch (e) {
      console.error('onWindowMessage() | failed: %o', e);
    }
  }

  onMessageFromWorld = (message) => {
    const {onTransform, onLegacyMessage, onCommand} = this.props;

    const hasCommand = Object.prototype.hasOwnProperty.call(message, 'command');
    if (hasCommand) {
      onCommand(message.command);
    }

    const hasRoute = Object.prototype.hasOwnProperty.call(message, 'route');
    if (hasRoute) {
      // FIXME:: Implement 3d voice
      // if (message.route && message.route === 'j') {
      // this.positionalVoice.localPosition = new
      // Position(data.value.x, data.value.y,
      // data.value.z,
      // data.value.filter);
      // this.setAllParticipantsVolume();
      // }
      if (message.route === 'j') {
        onTransform(message);
      } else {
        onLegacyMessage(message);
      }
    }
  };

  static sendMessage = (message) => {
    const {instance} = PlatformWorld;
    if (!instance) {
      return;
    }

    const {iframe} = instance;
    if (!iframe) {
      return;
    }

    const {contentWindow} = iframe;
    if (!contentWindow) {
      return;
    }

    contentWindow.postMessage(message, '*');
  };

  render() {
    const {location, isRemoteConfigLoaded} = this.props;
    const {iframeFailed} = this.state;

    console.log(
      '[WEBGL_DBG] PlatformWorld.render isRemoteConfigLoaded=' +
        isRemoteConfigLoaded +
        ' location=' +
        location,
    );

    if (iframeFailed) {
      return (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>Failed to load — Tap to retry</Text>
          <TouchableOpacity style={styles.retryButton} onPress={this.handleRetry}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      );
    }

    if (!isRemoteConfigLoaded) {
      return (
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Loading config...</Text>
        </View>
      );
    }

    const webScenesVersion = getRemoteConfigValue('web_scenes_version');
    const url = `${M3D_SCENES_BASE_URL}${webScenesVersion.asString()}/${location}/index.html`;
    console.log(
      '[WEBGL_DBG] iframe url=' +
        url +
        ' webScenesVersion=' +
        webScenesVersion.asString() +
        ' source=' +
        (typeof webScenesVersion.getSource === 'function'
          ? webScenesVersion.getSource()
          : '(no getSource)'),
    );

    return (
      <Iframe
        url={url}
        width="100%"
        height="100%"
        id="world-iframe"
        className="myClassname"
        display="initial"
        position="absolute"
        onLoad={this.handleIframeLoad}
      />
    );
  }
}

PlatformWorld.defaultProps = {
  onTransform: () => {},
  onLegacyMessage: () => {},
  onCommand: () => {},
  onAppearanceComplete: () => {},
  isRemoteConfigLoaded: false,
};

PlatformWorld.propTypes = {
  location: PropTypes.string.isRequired,
  onTransform: PropTypes.func,
  onLegacyMessage: PropTypes.func,
  onCommand: PropTypes.func,
  onAppearanceComplete: PropTypes.func,
  isRemoteConfigLoaded: PropTypes.bool,
};

const styles = StyleSheet.create({
  errorContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  errorText: {
    color: '#fff',
    fontSize: 16,
    marginBottom: 16,
  },
  retryButton: {
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#fff',
    borderRadius: 4,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 14,
  },
  loadingContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#000',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    color: '#fff',
    fontSize: 14,
  },
});

const mapStateToProps = (state) => ({
  isRemoteConfigLoaded: state.app.isRemoteConfigLoaded,
});

export default connect(mapStateToProps, null)(PlatformWorld);
